import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';
import * as Sentry from '@sentry/react';
import { getFriendlyErrorMessage } from '../utils/errorHandler';

const baseURL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
if (process.env.NODE_ENV === 'development') {
  console.log('🔧 API Base URL configurada:', baseURL);
}

// Configuração do cliente axios
const api = axios.create({
  baseURL: baseURL,
  timeout: 10000, // 10 segundos
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json'
  }
});

// Função para retry de requisições
const retryRequest = async (config: AxiosRequestConfig, retryCount = 0): Promise<AxiosResponse> => {
  const maxRetries = 3;
  const retryDelay = 1000 * Math.pow(2, retryCount); // Exponential backoff
  
  try {
    return await api(config);
  } catch (error) {
    const axiosError = error as AxiosError;
    const shouldRetry = retryCount < maxRetries && 
                       axiosError.response?.status && 
                       [408, 429, 500, 502, 503, 504].includes(axiosError.response.status);
    
    if (shouldRetry) {
      console.warn(`🔄 Tentativa ${retryCount + 1}/${maxRetries} falhou. Tentando novamente em ${retryDelay}ms...`);
      await new Promise(resolve => setTimeout(resolve, retryDelay));
      return retryRequest(config, retryCount + 1);
    }
    
    throw error;
  }
};

// Interceptor para adicionar token de autenticação
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    const fullURL = `${config.baseURL}${config.url}`;
    
    // Log detalhado apenas em desenvolvimento
    if (process.env.NODE_ENV === 'development') {
      console.log('🌐 Requisição:', {
        method: config.method?.toUpperCase(),
        url: fullURL,
        hasToken: !!token,
        data: config.data
      });
    }
    
    if (token) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    return config;
  },
  (error) => {
    console.error('❌ Erro no interceptor de requisição:', error);
    return Promise.reject(error);
  }
);

// Interceptor para tratar respostas e erros
api.interceptors.response.use(
  (response) => {
    // Log de sucesso apenas em desenvolvimento
    if (process.env.NODE_ENV === 'development') {
      console.log('✅ Resposta recebida:', {
        status: response.status,
        url: response.config.url,
        data: response.data
      });
    }
    return response;
  },
  async (error: AxiosError) => {
    const { response, config } = error;
    
    // Log detalhado do erro
    console.error('❌ Erro na requisição:', {
      status: response?.status,
      statusText: response?.statusText,
      url: config?.url,
      method: config?.method?.toUpperCase(),
      data: response?.data,
      message: error.message
    });
    
    // Obter mensagem amigável do erro
    const friendlyMessage = getFriendlyErrorMessage(error);

    // Adicionar mensagem amigável ao objeto de erro para uso posterior
    (error as any).friendlyMessage = friendlyMessage;

    // Reportar ao Sentry apenas erros 5xx (falhas reais do servidor) — 4xx
    // costuma ser validação/uso normal e só faria ruído no monitoramento.
    // O correlationId (devolvido pelo backend em toda resposta de erro)
    // amarra este evento ao log/erro correspondente do lado da API.
    if (response?.status && response.status >= 500) {
      const correlationId = (response.data as any)?.correlationId;
      Sentry.withScope((scope) => {
        if (correlationId) {
          scope.setTag('correlationId', correlationId);
        }
        scope.setTag('url', config?.url ?? 'desconhecida');
        scope.setTag('method', config?.method?.toUpperCase() ?? 'DESCONHECIDO');
        scope.setExtra('responseData', response.data);
        Sentry.captureException(error);
      });
    }

    // Tratamento específico por status
    if (response?.status === 401) {
      console.warn('🔐 Token expirado ou inválido. Redirecionando para login...');
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      
      // Evitar loop infinito se já estiver na página de login
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    } else if (response?.status === 403) {
      console.warn('🚫 Acesso negado:', friendlyMessage);
    } else if (response?.status === 404) {
      console.warn('🔍 Recurso não encontrado:', friendlyMessage);
    } else if (response?.status === 429) {
      console.warn('⏱️ Muitas requisições:', friendlyMessage);
    } else if (response?.status && response.status >= 500) {
      console.error('🔥 Erro interno do servidor:', friendlyMessage);
      // Para erros 500+, não redirecionar automaticamente - deixar componente tratar
    } else if (!response) {
      console.error('🌐 Erro de rede ou timeout:', friendlyMessage);
    }
    
    return Promise.reject(error);
  }
);

// Função helper para fazer requisições com retry automático
export const apiWithRetry = {
  get: (url: string, config?: AxiosRequestConfig) => 
    retryRequest({ ...config, method: 'GET', url }),
  
  post: (url: string, data?: any, config?: AxiosRequestConfig) => 
    retryRequest({ ...config, method: 'POST', url, data }),
  
  put: (url: string, data?: any, config?: AxiosRequestConfig) => 
    retryRequest({ ...config, method: 'PUT', url, data }),
  
  delete: (url: string, config?: AxiosRequestConfig) => 
    retryRequest({ ...config, method: 'DELETE', url }),
  
  patch: (url: string, data?: any, config?: AxiosRequestConfig) => 
    retryRequest({ ...config, method: 'PATCH', url, data })
};

// Função para verificar se a API está online
export const checkApiHealth = async (): Promise<boolean> => {
  try {
    const response = await api.get('/health', { timeout: 5000 });
    return response.status === 200;
  } catch (error) {
    console.error('💔 API não está respondendo:', error);
    return false;
  }
};

// Função para obter informações de conectividade
export const getConnectionInfo = () => {
  return {
    baseURL,
    isOnline: navigator.onLine,
    userAgent: navigator.userAgent,
    timestamp: new Date().toISOString()
  };
};

export default api;